/* eslint-disable jsx-a11y/anchor-is-valid */

import ja from "date-fns/locale/ja";
import dayjs from "dayjs";
import weekday from "dayjs/plugin/weekday";
import MicroModal from "micromodal";
import React, { FC, Fragment } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import NumberFormat from "react-number-format";
import SubscriptionDetailCSS from '../assets/scss/SubscriptionDetail.module.scss';
import { Link } from "react-router-dom";
import {
  deliveryTimesConstants,
  getModalOption, getOrderStatus, getPriceWithTax,
  Setting,
  statusMessages,
} from "../App";
import { Input, Label, Textarea } from "../components/CancelModalCss";
import { SpinButton } from "../components/SpinButton";
import { billingPolicyIntervals } from "../constants";
import { useSubscriptionDetail } from "../hooks/use-subscription-detail";
const TAX_RATE = 10;

registerLocale("ja", ja);

export type StatusMessage = keyof typeof statusMessages;
dayjs.extend(weekday);

export const SubscriptionDetail: FC<{ setting: Setting }> = ({ setting }) => {
  const {
    subscription,
    skip,
    skipCancel,
    updateStatus,
    deliveryDate,
    nextDeliveryDateChange,
    selectDeliveryTime,
    isDisableDays,
    updateDeliveryDays,
    updateStatusOpen,
    subscriptionLinePlan,
    paymentMethodUpdate,
    removeItem,
    onClickRemoveProduct,
    onClickRemoveAction,
    pageStatus,
    skipCount,
    setSkipCount,
    openNewOrderModal,
    onClickNewOrderAction,
    onClickQuantityModify,
    onClickQuantityAction,
    onChangeQuantity,
    quantityItem,
    openPlanModifyModal,
    onChangePlanModify,
    planModifyAction,
    planGroups,
    getPlanModifyList,
    getPlanModifyCycle,
    cancelSurveySetting,
    cancelSurveyInput,
    setCancelSurveyInput,
    openPaymentChange,
    allowModifyDay,
    selectNoDay,
    confirmDeliveryDays,
    isBusinessDayForCalendar,
  } = useSubscriptionDetail(setting);

  if (!subscription) {
    return <></>;
  }

  const deliveryTimes = deliveryTimesConstants.filter(
    (time) =>
      (time.value === "9" && subscription.shop.enableDeliveryTimesAM) ||
      (time.value === "12" && subscription.shop.enableDeliveryTimes12) ||
      (time.value === "14" && subscription.shop.enableDeliveryTimes14) ||
      (time.value === "16" && subscription.shop.enableDeliveryTimes16) ||
      (time.value === "18" && subscription.shop.enableDeliveryTimes18) ||
      (time.value === "19" && subscription.shop.enableDeliveryTimes19) ||
      (time.value === "20" && subscription.shop.enableDeliveryTimes20)
  );

  const {
    subscriptionContractId,
    billingPolicyInterval,
    billingPolicyIntervalCount,
    billingPolicyMinCycles,
    nextBillingDate,
    // customerId,
    status,
    deliveryPriceAmount,
    deliveryPriceCurrencyCode,
    deliveryFormattedAddress,
    paymentType,
  } = subscription;

  const billingAttempts = [
    ...subscription.billingAttempts.map((billingAttempt) => {
      let status;
      let isError = false;
      if (!billingAttempt.ready) {
        status = "実行中";
      } else if (billingAttempt.orderId) {
        status = "成功";
      } else {
        status = `失敗${"（" + billingAttempt.errorMessage! + "）"}`;
        isError = true;
      }

      return {
        orderId: billingAttempt.orderId,
        orderToken: billingAttempt.orderToken,
        orderName: billingAttempt.orderName,
        status,
        createdAt: billingAttempt.createdAt,
        isError,
        errorCode: billingAttempt.errorCode,
      };
    }),

    {
      orderId: subscription.originOrderId,
      orderToken: subscription.originOrderToken,
      orderName: subscription.originOrderName,
      status: "成功",
      createdAt: subscription.createdAt,
      isError: false,
      errorCode: null,
    },
  ];

  const skipArray = subscription
    ? Array.from({ length: subscription.shop.maxSkipCount }, (_, i) =>
        String(i + 1)
      )
    : [];
  const skipOptions = skipArray.map((v) => (
    <option value={v} selected={v === skipCount}>
      {v}
    </option>
  ));

  const subscriptionSkippedCount =
    subscription.subscriptionHistories.filter(
      (h) => h.status === "SKIPPED" && !h.canceledAt
    )[0]?.skipCount ?? 0;

  // 次回注文作成日
  const rewindNextBillingDate = dayjs(subscription.nextBillingDate);

  // 次回お届け日
  const rewindNextDeliveryDate = rewindNextBillingDate.add(
    subscription.deliveryDays,
    "day"
  );
  // スキップの取消は履歴がある＆もどった注文作成日が過去にならないこと
  const canSkipCancel =
    subscriptionSkippedCount && rewindNextBillingDate.isAfter(new Date());

  const rewindDate = subscription.shop?.showDelivery ? (
    <div>
      <h4>取り消し後のお届け予定日</h4>
      {deliveryDate && deliveryDate!.noDay ? (
        <p>指定なし</p>
      ) : (
        <p>
          {rewindNextDeliveryDate.format("YYYY年M月D日 ")}
          {
            deliveryTimesConstants[
              deliveryTimesConstants
                .map((time) => {
                  return time.value;
                })
                .indexOf(subscription.deliveryTime)
            ]?.label
          }
        </p>
      )}
      <p>
        {rewindNextDeliveryDate.format("YYYY年M月D日 ")}
        {
          deliveryTimesConstants[
            deliveryTimesConstants
              .map((time) => {
                return time.value;
              })
              .indexOf(subscription.deliveryTime)
          ]?.label
        }
      </p>
    </div>
  ) : (
    <div>
      <h4>取り消し後の次回支払い予定日</h4>
      <p>{rewindNextBillingDate.format("YYYY年M月D日 HH:mm頃")}</p>
    </div>
  );
  const resumeSubscriptionText = subscription.shop?.showDelivery ? (
    <div>
      <br />
      次回のお届け日は
      {deliveryDate && deliveryDate!.current
        ? deliveryDate!.noDay
          ? "指定なし"
          : deliveryDate!.current.format("YYYY年M月D日")
        : ""}
      です。
      <br />
      {subscription.shop.mypageChangeDeliveryDays &&
      subscription.shop.showDelivery
        ? "※再開後、「お届け予定日」からお届け日の変更ができます。"
        : ""}
    </div>
  ) : (
    <></>
  );
  // 解約時アンケートにチェックが入っていて、理由の選択肢が一つ以上設定されていれば解約アンケートが有効
  const cancelSurveyEnable =
    cancelSurveySetting.enable &&
    cancelSurveySetting.reasons &&
    cancelSurveySetting.reasons.length > 0
      ? true
      : false;

  const setCancelReason = (reason: string) => {
    const target = reason === cancelSurveyInput.selected ? undefined : reason;
    setCancelSurveyInput({ ...cancelSurveyInput, selected: target });
  };

  const cancelWithValidate = () => {
    if (
      cancelSurveyEnable &&
      cancelSurveySetting.require &&
      !cancelSurveyInput.selected
    ) {
      MicroModal.close(`modal-cancelled`);
      MicroModal.show("modal-cancelled-error", getModalOption(setting));
      return;
    }
    updateStatus("cancelled");
  };

  const closeErrorModal = (status: string) => {
    MicroModal.close(`modal-${status}-error`);
    MicroModal.show(`modal-${status}`, getModalOption(setting));
  };

  const select: JSX.Element[] = cancelSurveySetting.reasons
    ? cancelSurveySetting.reasons.map((reason, index) => {
        return (
          <Fragment key={index}>
            <Input
              type="radio"
              name="reason"
              id={index.toString()}
              value={reason}
              //onChange={() => setCancelSurveyInput({...cancelSurveyInput, selected: reason})}
              onClick={() => setCancelReason(reason)}
              checked={cancelSurveyInput.selected === reason}
            ></Input>
            <Label htmlFor={index.toString()}>{reason}</Label>
          </Fragment>
        );
      })
    : [];

  const cancelMainBody = cancelSurveyEnable ? (
    <main id="modal-cancelled-content">
      {cancelSurveySetting.leadText ? (
        <p>
          {cancelSurveySetting.leadText.split("\n").map((str) => (
            <>
              {str}
              <br />
            </>
          ))}
        </p>
      ) : (
        <></>
      )}
      <p>※既に決済が完了したご注文はキャンセルされません。</p>
      <h3>
        <b className={SubscriptionDetailCSS.CancelTitle}>
          解約理由について教えてください
          {cancelSurveySetting.require ? "（必須）" : ""}
        </b>
      </h3>
      <div id="selectButton">{select}</div>
      {cancelSurveySetting.extraField ? (
        <>
          <br />
          <h3>
            <b className={SubscriptionDetailCSS.CancelTitle}>その他、ご意見やご要望をご記入ください</b>
          </h3>
          <Textarea
            className={SubscriptionDetailCSS.Textarea}
            onChange={(e) =>
              setCancelSurveyInput({
                ...cancelSurveyInput,
                extraField: e.target.value,
              })
            }
            rows={5}
          ></Textarea>
        </>
      ) : (
        <></>
      )}
      <span style={{ color: 'red' }}>※３回お受け取りいただいていない場合、解約は無効となります</span>
    </main>
  ) : (
    <main id="modal-cancelled-content">
      <p>
        定期購買を解約します。
        <br />
        ※既に決済が完了したご注文はキャンセルされません。
      </p>
    </main>
  );

  const deliveryDateNoDay = subscription.shop.enableDeliveryDateNoDay ? (
    <div className="checkbox">
      <input
        type="checkbox"
        id="modal-delivery-days-min"
        checked={deliveryDate ? deliveryDate!.noDay : false}
        onChange={(evt) => {
          selectNoDay(evt.target);
        }}
      />
      <label htmlFor="modal-delivery-days-min">指定なし</label>
    </div>
  ) : (
    <></>
  );

  // TODO
  const deliveryPriceCurrency =
    deliveryPriceCurrencyCode === "JPY" ? "円" : deliveryPriceCurrencyCode;

  let skipButton = <></>;
  let skipCancelButton = <></>;
  let pauseOrActiveButton = <></>;
  let cancelButton = <></>;
  const showSkip =
    !subscription.shop.hideLowestBuySkip ||
    billingPolicyMinCycles <= billingAttempts.length;
  const showPause =
    !subscription.shop.hideLowestBuyPause ||
    billingPolicyMinCycles <= billingAttempts.length;
  const showCancel =
    !subscription.shop.hideLowestBuyCancel ||
    billingPolicyMinCycles <= billingAttempts.length;
  const showCancelAlert =
    !subscription.shop.hideLowestBuyCancel &&
    billingPolicyMinCycles > billingAttempts.length;

  if (subscription.status !== "CANCELLED") {
    const [action, name] =
      subscription.status === "ACTIVE"
        ? ["paused", "一時停止"]
        : ["active", "再開"];

    pauseOrActiveButton =
      subscription.shop.showPause && showPause ? (
        <button
          className="huckleberry-subscription-detail-order-btn stop"
          disabled={subscription.disabled}
          onClick={() => {
            updateStatusOpen(action);
          }}
        >
          {name}
        </button>
      ) : (
        <></>
      );

    skipButton =
      subscription.shop.showSkip && showSkip ? (
        <button
          className="huckleberry-subscription-detail-order-btn skip"
          disabled={subscription.disabled}
          onClick={() => {
            updateStatusOpen("skip");
          }}
        >
          次回のお届けをスキップ
        </button>
      ) : (
        <></>
      );

    skipCancelButton =
      subscription.shop.showSkip && canSkipCancel ? (
        <button
          className="huckleberry-subscription-detail-skip-reset-btn"
          disabled={subscription.disabled}
          onClick={() => {
            updateStatusOpen("skip-cancel");
          }}
        >
          スキップの取り消し
        </button>
      ) : (
        <></>
      );

    const validBillingAttempts = billingAttempts.filter((billingAttempt) => !billingAttempt.errorCode || false)  
    const disabledCancelButton = subscription.billingPolicyMinCycles > validBillingAttempts.length;
    cancelButton =
      subscription.shop.showCancel && showCancel ? (
        <>
          <a className={disabledCancelButton ? 'button-disabled' : 'cancel-button'} onClick={() => {
            if (disabledCancelButton) return;
            showCancelAlert
                ? updateStatusOpen("cancel-alert")
                : updateStatusOpen("cancelled");
          }}
          >
            定期お届け便を解約
          </a>
        </>
      ) : (
        <></>
      );
  }

  const address =
    deliveryFormattedAddress && deliveryFormattedAddress.length > 0 ? (
      <p
        dangerouslySetInnerHTML={{
          __html: deliveryFormattedAddress.join("</br>"),
        }}
      />
    ) : (
      <p>なし</p>
    );

  const addressButton =
    subscription.status !== "CANCELLED" &&
    subscription.shop.showChangeAddress ? (
      <Link
        className="subscr-item-btn--v2 subscr-item-btn-grey"
        to={`/subscriptions/${encodeURIComponent(
          subscription.subscriptionContractId
        )}/edit`}
      >
        変更
      </Link>
    ) : (
      <></>
    );

  const mypageRemarks = subscription.shop.mypageRemarks ? (
    <div className="huckleberry-subscription-detail-memo">
      <p>{subscription.shop.mypageRemarks}</p>
    </div>
  ) : (
    <></>
  );

  const rank =
    subscription.shop.rankGroup?.public && subscription.rank?.name ? (
      <div className="huckleberry-subscription-list-rank">
        <span className="item rank">{subscription.rank.name}</span>
        <span className="item rank-benefits">
          {subscription.rank?.discountPercentage}%割引
        </span>
      </div>
    ) : (
      <></>
    );

  let deliveryDaysView = <></>;
  let deliveryHeaderText = <></>;
  let deleveryChangeDeliveryDaysButton = <></>;
  if (
    subscription.shop.showDelivery ||
    subscription.shop.mypageChangeDeliveryDays
  ) {
    deliveryHeaderText = (
      <header>
        <h2 className="huckleberry-subscription-detail-schedule-title">
          お届け予定日
        </h2>
      </header>
    );
  }

  if (
    subscription.shop.mypageChangeDeliveryDays &&
    subscription.shop.showDelivery
  ) {
    deleveryChangeDeliveryDaysButton = (
      <div
        className="subscr-item-btn--v2 subscr-item-btn-grey"
        onClick={() => {
          updateStatusOpen("delivery-days");
        }}
      >
        変更
      </div>
    );
  }
  let productNewAddAction = <></>;
  if (subscription.shop.showNewAddProduct) {
    productNewAddAction = (
      <Link
        to={`/subscriptions/${encodeURIComponent(
          subscriptionContractId
        )}/lines/new`}
      >
        <span className="huckleberry-subscription-detail-btn new-add">
          他の商品を定期購買に追加する
        </span>
      </Link>
    );
  }

  const productCount = subscription.subscriptionLines.length;

  let newOrderAction = <></>;
  if (subscription.shop.showNewOrder) {
    newOrderAction = (
      <button
        className="huckleberry-subscription-detail-btn add"
        onClick={openNewOrderModal}
      >
        追加購入する
      </button>
    );
  }

  const modifyList = (getPlanModifyList() || []).sort((a, b) => {
    const daysA = parseInt(a.name.replace(/[^0-9]/g, ""), 10) || 0; // "75日" -> 75
    const daysB = parseInt(b.name.replace(/[^0-9]/g, ""), 10) || 0; // "30日" -> 30
    return daysA - daysB; // 昇順に並び替え
  });

  const planModifyButton =
    subscription.shop.showPlanModify &&
    !(
      subscription.shop.hideLowestModify &&
      subscription.billingPolicyMinCycles > 0
    ) ? (
      <div
        className="subscr-item-btn--v2 subscr-item-btn-grey"
        onClick={openPlanModifyModal}
      >
        変更
      </div>
    ) : (
      <></>
    );

  let planGroupOptions = [<></>];
  if (planGroups.length) {
    planGroupOptions = (modifyList || []).map((plan) => {
      return (
        <option value={plan.planId}>
          {plan.billingPolicyIntervalCount}
          {billingPolicyIntervals[plan.billingPolicyInterval]}
        </option>
      );
    });
  }
  const subtotal = subscription.subscriptionLines.reduce(
    (total: number, line) => {
      return total + line.currentPriceAmount * line.quantity;
    },
    0
  );
  const tax = Math.floor((subtotal * TAX_RATE) / 100);
  const deliveryTimeLabel = deliveryTimesConstants[
      deliveryTimesConstants
          .map((time) => {
            return time.value;
          })
          .indexOf(subscription.deliveryTime)
      ]?.label

  const paymentChangeView = subscription.shop!.showChangePayment ? (
    <div
      className={`subscr-item-btn--v2 subscr-item-btn-grey subscr-item-btn--payment ${deliveryTimeLabel ? 'has-delivery-time' : ''}`}
      onClick={openPaymentChange}
    >
      変更
    </div>
  ) : (
    <></>
  );

  let addMoreProductAction = <></>;
  if (planGroups.length > 0 && subscription.status !== "CANCELLED") {
    addMoreProductAction = (
        <Link
            to={`/subscriptions/${encodeURIComponent(
                subscriptionContractId
            )}/lines/add`}
        >
          <span className="mp-btn-cm medium gray-cm">
            製品を追加
          </span>
        </Link>
    );
  }

  return (
      <>
        <div className="shell mp-subscriptiondetail-page-wrapper">
          <div className="mp-subscriptiondetail-page">
            <div className="mlh-title-global mlh-title-global--v2">SUBSCRIPTION DETAILS<span>定期お届け便ご注文詳細</span>
            </div>
            <div className="mp-status-line no-line">
              <div className="mp-stt-line-title">{getOrderStatus(subscription)}</div>
              <div className="mp-stt-line-info">
                <div className="stt-line-row stt-line-row__status">
                  <div className="stt-lr-ttl large">お申込み日</div>
                  <div className="stt-lr-txt">{dayjs(subscription.createdAt).format("YYYY.MM.DD")}</div>
                </div>
                <div className="stt-line-row stt-line-row__status">
                  <div className="stt-lr-ttl large">お申込み番号</div>
                  <div className="stt-lr-txt">{subscription.subscriptionContractId?.split("/")[4]}</div>
                </div>
              </div>
            </div>

            <div className="subscr-wrap">
              <div className="subscr-title-global">お申込み内容</div>
              <div className="subscr-box">
                <div className="subscr-item subscr-item--v4">
                  <div className="subscr-item-desc subscr-item-desc--v2">
                    <div className="subscr-item-info--v2">
                      <div className="subscr-item--v2">
                        <div className="subscr-item--left">お届けサイクル</div>
                        <div className="subscr-item--right">
                          {billingPolicyIntervalCount +
                              billingPolicyIntervals[billingPolicyInterval]}
                        </div>
                      </div>
                      <div className="subscr-item--v2 subscr-item--v3">
                        <div className="subscr-item--delivery">
                          <div>次回お届け予定日</div>
                          <div>
                            {status === "ACTIVE" ? (
                                deliveryDate?.noDay ? (
                                    "指定なし"
                                ) : (
                                    <>
                                      {deliveryDate?.current.format("YYYY年MM月DD日")}
                                      {deliveryTimeLabel && <span> ({deliveryTimeLabel})</span>}
                                    </>
                                )
                            ) : (
                                "-"
                            )}
                          </div>
                        </div>
                        <div className="subscr-item--rewind">
                          <div>変更締め切り日</div>
                          <div>{rewindNextBillingDate.subtract(1, 'd').format('YYYY年MM月DD日 HH:mm')}</div>
                        </div>
                      </div>
                      <div className="subscr-item--v2">
                        <div className="subscr-item--left">支払い方法</div>
                        <div className="subscr-item--right">
                          {subscription.paymentType}
                        </div>
                      </div>
                    </div>
                    <div className="subscr-item-btn subscr-item-btn--v3">
                      <div className="subscr-item-btn-grey-box">
                        {planModifyButton}
                        {deleveryChangeDeliveryDaysButton}
                        {paymentChangeView}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="subscr-box">
                <div className="order-information-box or-detail">
                  <div className="order-inf-ttl">ご注文の製品</div>
                  <div className="or-detail-box">
                    {subscription.subscriptionLines.map((line, index) => {
                      // TODO
                      const currency =
                          line.lineDiscountedPriceCurrencyCode === "JPY"
                              ? "円"
                              : line.lineDiscountedPriceCurrencyCode;
                      const title = line.variantTitle
                          ? `${line.title} - ${line.variantTitle}`
                          : line.title;

                      let productEditAction = <></>;
                      if (
                          subscriptionLinePlan &&
                          subscriptionLinePlan.find(
                              (plan) => plan.lineId === line.lineId
                          ) &&
                          subscription.shop.showChangeProduct &&
                          !(
                              subscription.shop.hideLowestModify &&
                              subscription.billingPolicyMinCycles > 0
                          )
                      ) {
                        productEditAction = (
                            <Link
                                to={`/subscriptions/${encodeURIComponent(
                                    subscriptionContractId
                                )}/lines/${encodeURIComponent(line.lineId)}`}
                            >
                              <span className="mp-btn-cm medium gray-cm">
                                製品を変更
                              </span>
                            </Link>
                        );
                      }

                      let productRemoveAction = <></>;
                      if (
                          subscription.shop.showRemoveProduct &&
                          productCount > 1 &&
                          !(
                              subscription.shop.hideLowestModify &&
                              subscription.billingPolicyMinCycles > 0
                          )
                      ) {
                        productRemoveAction = (
                            <a
                                onClick={(e) => {
                                  onClickRemoveProduct(line);
                                }}
                                className="mp-btn-cm medium white-cm"
                            >
                              削除
                            </a>
                        );
                      }

                      let productQuantityAction = <></>;
                      if (
                          subscription.shop.showChangeQuantity &&
                          planGroups.length > 0
                      ) {
                        productQuantityAction = (
                            <a
                                className="mp-btn-cm medium gray-cm"
                                onClick={() => {
                                  onClickQuantityModify(line);
                                }}
                            >
                              点数を変更
                            </a>
                        );
                      }

                      return (
                          <div className="or-detail-box-row" key={index}>
                            <div className="or-detail-left">
                              <div className="mp-list-products">
                                <div className="mp-row-product">
                                  {line.variantImage && <a className="mp-row-product-thumb">
                                    <img
                                        src={line.variantImage}
                                        alt={title}/>
                                  </a>}
                                  <div className="mp-row-product-info">
                                    <div className="pro-inf-ttl">{title}</div>
                                    <div className="pro-inf-stt">
                                      <div className="stt-row">単価：¥{getPriceWithTax(line.currentPriceAmount)} 税込</div>
                                      <div className="stt-row">数量：{line.quantity}</div>
                                    </div>
                                    <div className="pro-inf-price">¥{getPriceWithTax(line.currentPriceAmount * line.quantity)} <small>税込</small></div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="or-detail-right">
                              <div className="mp-button-box">
                                {productEditAction}
                                {productQuantityAction}
                                {productRemoveAction}
                              </div>
                            </div>
                          </div>
                      );
                    })}
                  </div>
                </div>

                <div className="order-information-box or-payment">
                  <div className="order-inf-box">
                    <div className="order-inf-row">
                      <div className="inf-row-ttl">商品合計</div>
                      <div className="inf-row-txt"><span className="ff-s">¥{getPriceWithTax(subtotal)}</span></div>
                    </div>
                    <div className="order-inf-row">
                      <div className="inf-row-ttl">送料（ヤマト運輸）</div>
                      <div className="inf-row-txt"><span className="ff-s">¥{deliveryPriceAmount.toLocaleString()}</span></div>
                    </div>
                    <div className="order-inf-row">
                      <div className="inf-row-ttl">支払い金額（税込）</div>
                      <div className="inf-row-txt"><span className="ff-s t-price">¥{(subtotal + deliveryPriceAmount).toLocaleString()}</span></div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mp-button-box mp-button-add-more">
                {addMoreProductAction}
              </div>

              <div className="subscr-title-global">お届け先情報</div>
              <div className="subscr-box">
                <div className="subscr-item subscr-item--v2 subscr-item--v4">
                  <div className="subscr-item-desc subscr-item-desc--v2">
                    <div className="subscr-item-info--v2 subscr-item-info--v3">
                      {address}
                    </div>
                    <div className="subscr-item-btn subscr-item-btn--v3">
                      <div className="subscr-item-btn-grey-box">
                        {addressButton}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="btn-mlh-bottom cancel-delivery-btn">
                {cancelButton}
              </div>
              <div className="subscr-bottom">
                ■ ご注文内容の変更は、次回ご注文日の【1日前】までとなっております。締め切り後の変更はできません。詳しくは、ご利用ガイドをご覧ください。<br/>
                ※ 次回ご注文日は、次回お届けの予定日の【7日前】になります。<br/>
                ■ 2回目以降のお届け日につきましては、初回のお届け日時とお届けサイクルに合わせて自動的に設定しております。<br/>
                ■ 配送時の交通事情や天候などにより遅延が発生する場合がございますので、あらかじめご了承ください。
              </div>

              <div className="btn-mlh-bottom btn-subscr">
                <a className="mlh-hover" href={`${setting.referrer}/account`} target="_parent" title="マイページへ戻る">
                  マイページへ戻る
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="micromodal" id="modal-delivery-days" aria-hidden="true">
          <div className="micromodal-overlay" tabIndex={-1}>
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-delivery-days-title"
            >
              <header>
                <button aria-label="Close modal" data-micromodal-close></button>
                <h2 id="modal-delivery-days-title">お届け予定日の変更</h2>
              </header>
              <main id="modal-delivery-days-content">
                <p>
                  次回ご注文のお届け予定日を変更します。
                  <br />
                  ご希望の日を選んで「保存」してください。
                </p>
                {deliveryDateNoDay}
                <div className="litepicker-wrap">
                  <DatePicker
                      locale="ja"
                      selected={
                        deliveryDate && !deliveryDate!.noDay
                            ? deliveryDate!.change.toDate()
                            : undefined
                      }
                      minDate={
                        deliveryDate ? deliveryDate!.min.toDate() : undefined
                      }
                      maxDate={
                        deliveryDate && deliveryDate!.max
                            ? deliveryDate!.max.toDate()
                            : undefined
                      }
                      onChange={nextDeliveryDateChange}
                      filterDate={isBusinessDayForCalendar}
                      inline={true}
                      disabled={
                        deliveryDate
                            ? deliveryDate!.noDay &&
                            !subscription.shop.enableDeliveryDateNoDay
                            : false
                      }
                      disabledKeyboardNavigation
                  />
                </div>

                <p style={{ margin: "30px 0 10px" }}>お届け時間の設定</p>

                <select
                    onChange={(event) => selectDeliveryTime(event.target.value)}
                    disabled={deliveryDate ? deliveryDate!.noDay : false}
                >
                  {[{ value: "", label: "指定なし" }]
                      .concat(
                        subscription.shop.deliveryTimes.map((deliveryTime) => ({
                          label: deliveryTime.title,
                          value: deliveryTime.tag,
                        }))
                      )
                      .map((time) => {
                        return (
                            <option
                                value={!time.value ? "" : time.value}
                                selected={
                                  deliveryDate
                                      ? deliveryDate.time === time.value
                                      : time.value === subscription.deliveryTime
                                }
                            >
                              {time.label}
                            </option>
                        );
                      })}
                </select>
              </main>
              <footer>
                <button className="btn" onClick={confirmDeliveryDays}>
                  保存
                </button>
              </footer>
            </div>
          </div>
        </div>
        <div
            className="micromodal"
            id="modal-delivery-days-complete"
            aria-hidden="true"
        >
          <div className="micromodal-overlay" tabIndex={-1}>
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-delivery-days-complete-title"
            >
              <header>
                <button aria-label="Close modal" data-micromodal-close></button>
                <h2 id="modal-delivery-days-complete-title">
                  お届け予定日の変更
                </h2>
              </header>
              <main id="modal-delivery-days-complete-content">
                <p>
                  次回ご注文のお届け予定日を変更しました。
                  <br />
                  次回お支払い日： {dayjs(nextBillingDate).format("YYYY年M月D日")}
                  <br />
                  次回お届け予定日：{" "}
                  {deliveryDate
                      ? deliveryDate.noDay
                          ? "指定なし"
                          : deliveryDate.change.format("YYYY年M月D日")
                      : ""}{" "}
                  {deliveryDate && deliveryDate.noDay
                      ? ""
                      : deliveryTimesConstants[
                          deliveryTimesConstants
                              .map((time) => {
                                return time.value;
                              })
                              .indexOf(deliveryDate?.time ?? "")
                          ]?.label}
                </p>
              </main>
              <footer>
                <button className="btn" data-micromodal-close>
                  ＯＫ
                </button>
              </footer>
            </div>
          </div>
        </div>

        <div className="micromodal" id="modal-skip" aria-hidden="true">
          <div className="micromodal-overlay" tabIndex={-1}>
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-skip-title"
            >
              <header>
                <button aria-label="Close modal" data-micromodal-close></button>
                <h2 id="modal-skip-title">定期購入のスキップ</h2>
              </header>
              <main id="modal-skip-content">
                <p>スキップする回数を選択してください。</p>
                <select
                    value={skipCount}
                    onChange={(e) => {
                      setSkipCount(e.target.value);
                    }}
                >
                  {skipOptions}
                </select>
              </main>
              <footer>
                <button className="btn" onClick={skip}>
                  スキップ
                </button>
              </footer>
            </div>
          </div>
        </div>
        <div className="micromodal" id="modal-skip-complete" aria-hidden="true">
          <div className="micromodal-overlay" tabIndex={-1}>
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-skip-complete-title"
            >
              <header>
                <button aria-label="Close modal" data-micromodal-close></button>
                <h2 id="modal-skip-complete-title">定期購入のスキップ</h2>
              </header>
              <main id="modal-skip-complete-content">
                <p>
                  スキップを受け付けました。
                  <br />
                </p>
              </main>
              <footer>
                <button className="btn" data-micromodal-close>
                  ＯＫ
                </button>
              </footer>
            </div>
          </div>
        </div>

        <div className="micromodal" id="modal-skip-cancel" aria-hidden="true">
          <div className="micromodal-overlay" tabIndex={-1}>
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-skip-cancel-title"
            >
              <header>
                <button aria-label="Close modal" data-micromodal-close></button>
                <h2 id="modal-skip-cancel-title">スキップの取り消し</h2>
              </header>
              <main id="modal-skip-content">
                <p>スキップを取り消します。よろしいですか？</p>
                {rewindDate}
              </main>
              <footer>
                <button className="btn" onClick={skipCancel}>
                  スキップの取り消し
                </button>
              </footer>
            </div>
          </div>
        </div>
        <div
            className="micromodal"
            id="modal-skip-cancel-complete"
            aria-hidden="true"
        >
          <div className="micromodal-overlay" tabIndex={-1}>
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-skip-cancel-complete-title"
            >
              <header>
                <button aria-label="Close modal" data-micromodal-close></button>
                <h2 id="modal-skip-cancel-complete-title">スキップの取り消し</h2>
              </header>
              <main id="modal-skip-cancel-complete-content">
                <p>
                  スキップを取り消しました。
                  <br />
                </p>
              </main>
              <footer>
                <button className="btn" data-micromodal-close>
                  ＯＫ
                </button>
              </footer>
            </div>
          </div>
        </div>

        <div className="micromodal" id="modal-paused" aria-hidden="true">
          <div className="micromodal-overlay" tabIndex={-1}>
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-paused-title"
            >
              <header>
                <button aria-label="Close modal" data-micromodal-close></button>
                <h2 id="modal-paused-title">定期購買の一時停止</h2>
              </header>
              <main id="modal-paused-content">
                <p>定期購買を一時停止しますか？</p>
              </main>
              <footer>
                <button
                    className="btn"
                    onClick={() => {
                      updateStatus("paused");
                    }}
                >
                  一時停止
                </button>
              </footer>
            </div>
          </div>
        </div>
        <div className="micromodal" id="modal-paused-complete" aria-hidden="true">
          <div className="micromodal-overlay" tabIndex={-1}>
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-paused-complete-title"
            >
              <header>
                <button aria-label="Close modal" data-micromodal-close></button>
                <h2 id="modal-paused-complete-title">定期購買の一時停止</h2>
              </header>
              <main id="modal-paused-complete-content">
                <p>
                  定期購買を一時停止しました。
                  <br />
                  再開は注文内容から行えます。
                </p>
              </main>
              <footer>
                <button className="btn" data-micromodal-close>
                  ＯＫ
                </button>
              </footer>
            </div>
          </div>
        </div>

        <div className="micromodal" id="modal-active" aria-hidden="true">
          <div className="micromodal-overlay" tabIndex={-1}>
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-active-title"
            >
              <header>
                <button aria-label="Close modal" data-micromodal-close></button>
                <h2 id="modal-actove-title">定期購買の再開</h2>
              </header>
              <main id="modal-active-content">
                <p>
                  定期購買を再開しますか？
                  {resumeSubscriptionText}
                </p>
              </main>
              <footer>
                <button
                    className="btn"
                    onClick={() => {
                      updateStatus("active");
                    }}
                >
                  再開
                </button>
              </footer>
            </div>
          </div>
        </div>
        <div className="micromodal" id="modal-active-complete" aria-hidden="true">
          <div className="micromodal-overlay" tabIndex={-1}>
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-active-complete-title"
            >
              <header>
                <button aria-label="Close modal" data-micromodal-close></button>
                <h2 id="modal-active-complete-title">定期購買の再開</h2>
              </header>
              <main id="modal-active-complete-content">
                <p>
                  定期購買を再開しました。
                  <br />
                  次回お支払い日：
                  {dayjs(nextBillingDate).format("YYYY年M月D日")}
                  <br />
                  {subscription.shop?.showDelivery &&
                  deliveryDate &&
                  deliveryDate!.current
                      ? "次回お届け予定日：" +
                      deliveryDate!.current.format("YYYY年M月D日")
                      : ""}
                </p>
              </main>
              <footer>
                <button className="btn" data-micromodal-close>
                  ＯＫ
                </button>
              </footer>
            </div>
          </div>
        </div>

        <div className="micromodal" id="modal-cancelled" aria-hidden="true">
          <div className="micromodal-overlay" tabIndex={-1}>
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-cancelled-title"
            >
              <header>
                <button aria-label="Close modal" data-micromodal-close></button>
                <h2 id="modal-cancelled-title">定期購買の解約</h2>
              </header>
              {cancelMainBody}
              <footer>
                <button
                    className="btn"
                    onClick={() => {
                      cancelWithValidate();
                    }}
                >
                  解約
                </button>
                <button className="btn clear" data-micromodal-close>
                  戻る
                </button>
              </footer>
            </div>
          </div>
        </div>

        <div className="micromodal" id="modal-cancelled-error" aria-hidden="true">
          <div className="micromodal-overlay" tabIndex={-1}>
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-cancelled-error-title"
            >
              <main id="modal-cancelled-error-content">
                <h2 id="modal-cancelled-error-title">エラー</h2>
                <p>解約理由を選択してください</p>
              </main>
              <footer>
                <button
                    className="btn"
                    onClick={() => closeErrorModal("cancelled")}
                >
                  ＯＫ
                </button>
              </footer>
            </div>
          </div>
        </div>

        <div
            className="micromodal"
            id="modal-cancelled-complete"
            aria-hidden="true"
        >
          <div className="micromodal-overlay" tabIndex={-1}>
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-cancelled-complete-title"
            >
              <header>
                <button aria-label="Close modal" data-micromodal-close></button>
                <h2 id="modal-cancelled-complete-title">定期購買の解約</h2>
              </header>
              <main id="modal-cancelled-complete-content">
                <p>
                  定期購買を解約しました。
                  <br />
                  再度ご注文される場合は、もう一度定期購買をご注文ください。
                  <br />
                </p>
              </main>
              <footer>
                <button className="btn" data-micromodal-close>
                  ＯＫ
                </button>
              </footer>
            </div>
          </div>
        </div>

        <div className="micromodal" id="modal-cancel-alert" aria-hidden="true">
          <div className="micromodal-overlay" tabIndex={-1}>
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-cancel-alert-title"
            >
              <header>
                <button aria-label="Close modal" data-micromodal-close></button>
                <h2 id="modal-cancel-alert-title">
                  定期購買の解約手続きを進めることができません
                </h2>
              </header>
              <main id="modal-cancel-alert-content">
                <p>
                  最低購入回数（{billingPolicyMinCycles}
                  回）に達していないため、定期購買の解約手続きを進めることができません。
                  <br />
                  あと{billingPolicyMinCycles - billingAttempts.length}
                  回ご購入いただいたのち、解約手続きを行うことができます。
                  <br />
                </p>
              </main>
              <footer>
                <button className="btn" data-micromodal-close>
                  ＯＫ
                </button>
              </footer>
            </div>
          </div>
        </div>

        <div className="micromodal" id="modal-remove-product" aria-hidden="true">
          <div className="micromodal-overlay" tabIndex={-1}>
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-remove-product-title"
            >
              <header>
                <button aria-label="Close modal" data-micromodal-close></button>
                <h2 id="modal-remove-product-title">商品の削除</h2>
              </header>
              <main id="modal-remove-product-content">
                <p>
                  「{removeItem ? removeItem.title : ""}
                  」を削除します。よろしいですか？
                </p>
              </main>
              <footer>
                <button
                    className="btn"
                    onClick={onClickRemoveAction}
                    disabled={pageStatus.removeAction}
                >
                  削除
                </button>
                <button className="btn clear" data-micromodal-close>
                  戻る
                </button>
              </footer>
            </div>
          </div>
        </div>

        <div
            className="micromodal"
            id="modal-remove-product-complete"
            aria-hidden="true"
        >
          <div className="micromodal-overlay" tabIndex={-1}>
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-remove-product-complete-title"
            >
              <header>
                <button aria-label="Close modal" data-micromodal-close></button>
                <h2 id="modal-remove-product-complete-title">商品の削除</h2>
              </header>
              <main id="modal-remove-product-complete-content">
                <p>「{removeItem ? removeItem.title : ""}」を削除しました。</p>
              </main>
              <footer>
                <button className="btn" data-micromodal-close>
                  ＯＫ
                </button>
              </footer>
            </div>
          </div>
        </div>

        <div className="micromodal" id="modal-quantity-modify" aria-hidden="true">
          <div className="micromodal-overlay" tabIndex={-1}>
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-quantity-modify-title"
            >
              <header>
                <button aria-label="Close modal" data-micromodal-close></button>
                <h2 id="modal-quantity-modify-title">数量の変更</h2>
              </header>
              <main id="modal-quantity-modify-content">
                <p>
                  数量を選択してください。
                  <br />
                  変更は次回のご注文から反映されます。
                </p>
                <SpinButton
                    min={1}
                    value={quantityItem ? quantityItem.quantity : 1}
                    onChange={onChangeQuantity}
                />
              </main>
              <footer>
                <button
                    className="btn"
                    disabled={pageStatus.quantityAction}
                    onClick={onClickQuantityAction}
                >
                  保存
                </button>
              </footer>
            </div>
          </div>
        </div>
        <div
            className="micromodal"
            id="modal-quantity-modify-complete"
            aria-hidden="true"
        >
          <div className="micromodal-overlay" tabIndex={-1}>
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-quantity-modify-complete-title"
            >
              <header>
                <button aria-label="Close modal" data-micromodal-close></button>
                <h2 id="modal-quantity-modify-complete-title">数量の変更</h2>
              </header>
              <main id="modal-quantity-modify-complete-content">
                <p>
                  数量の変更を受け付けました。
                  <br />
                  次回のご注文から反映されます。
                </p>
              </main>
              <footer>
                <button className="btn" data-micromodal-close>
                  ＯＫ
                </button>
              </footer>
            </div>
          </div>
        </div>
        <div className="micromodal" id="modal-error" aria-hidden="true">
          <div className="micromodal-overlay" tabIndex={-1}>
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-error-title"
            >
              <header>
                <button aria-label="Close modal" data-micromodal-close></button>
                <h2 id="modal-error-title">エラー</h2>
              </header>
              <main id="modal-error-content">
                <p>エラーが発生しました・カスタマーサポートへお問合せください。</p>
              </main>
              <footer>
                <button className="btn" data-micromodal-close>
                  ＯＫ
                </button>
              </footer>
            </div>
          </div>
        </div>

        <div className="micromodal" id="modal-new-order" aria-hidden="true">
          <div className="micromodal-overlay" tabIndex={-1}>
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-new-order-title"
            >
              <header>
                <button aria-label="Close modal" data-micromodal-close></button>
                <h2 id="modal-new-order-title">追加注文</h2>
              </header>
              <main id="modal-new-order-content">
                <p>
                  以下の商品を追加注文します。よろしいですか？
                  <br />
                  ※追加注文は通常のお届けとは別で注文します。
                </p>
                <section>
                  {subscription.subscriptionLines.map((line) => {
                    // TODO
                    const currency =
                        line.lineDiscountedPriceCurrencyCode === "JPY"
                            ? "円"
                            : line.lineDiscountedPriceCurrencyCode;
                    const title = line.variantTitle
                        ? `${line.title} - ${line.variantTitle}`
                        : line.title;
                    return (
                        <article key={line.lineId} style={{ margin: "30px 0" }}>
                          <dl className="huckleberry-subscription-detail-product-detail">
                            <dt className="huckleberry-subscription-detail-product-title">
                              商品
                            </dt>
                            <dd className="huckleberry-subscription-detail-product-name">
                              {title}{" "}
                            </dd>
                          </dl>
                          <dl className="huckleberry-subscription-detail-product-detail">
                            <dt className="label">SKU</dt>
                            <dd className="value">{line.sku}</dd>
                          </dl>
                          <dl className="huckleberry-subscription-detail-product-detail">
                            <dt className="label">価格</dt>
                            <dd className="value">
                              <NumberFormat
                                  displayType="text"
                                  value={line.currentPriceAmount}
                                  thousandSeparator={true}
                                  suffix={currency}
                              />
                            </dd>
                          </dl>
                          <dl className="huckleberry-subscription-detail-product-detail">
                            <dt className="label">個数</dt>
                            <dd className="value">
                              <NumberFormat
                                  displayType="text"
                                  value={line.quantity}
                                  thousandSeparator={true}
                              />
                            </dd>
                          </dl>
                          <dl className="huckleberry-subscription-detail-product-detail">
                            <dt className="label">小計</dt>
                            <dd className="value">
                              <NumberFormat
                                  displayType="text"
                                  value={line.currentPriceAmount * line.quantity}
                                  thousandSeparator={true}
                                  suffix={currency}
                              />
                            </dd>
                          </dl>
                        </article>
                    );
                  })}
                  <article
                      style={{ margin: "30px 0", borderTop: "1px solid #000" }}
                  >
                    <dl className="huckleberry-subscription-detail-product-summary">
                      <dt className="label">送料</dt>
                      <dd className="value">
                        <NumberFormat
                            displayType="text"
                            value={deliveryPriceAmount}
                            thousandSeparator={true}
                            suffix={deliveryPriceCurrency}
                        />
                      </dd>
                    </dl>
                    <dl className="huckleberry-subscription-detail-product-summary">
                      <dt className="label">合計</dt>
                      <dd className="value">
                        <NumberFormat
                            displayType="text"
                            value={subscription.subscriptionLines.reduce(
                                (total, line) =>
                                    total + line.currentPriceAmount * line.quantity,
                                deliveryPriceAmount
                            )}
                            thousandSeparator={true}
                            suffix={deliveryPriceCurrency}
                        />
                      </dd>
                    </dl>
                  </article>
                  <p style={{ color: "#ff0000" }}>
                    ※税金、割引金額は実際の決済時に計算されます。
                  </p>
                </section>
              </main>
              <footer>
                <button
                    className="btn"
                    onClick={onClickNewOrderAction}
                    disabled={pageStatus.newOrderAction}
                >
                  注文
                </button>
                <button className="btn clear" data-micromodal-close>
                  戻る
                </button>
              </footer>
            </div>
          </div>
        </div>

        <div
            className="micromodal"
            id="modal-new-order-complete"
            aria-hidden="true"
        >
          <div className="micromodal-overlay" tabIndex={-1}>
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-new-order-complete-title"
            >
              <header>
                <button aria-label="Close modal" data-micromodal-close></button>
                <h2 id="modal-new-order-complete-title">追加注文</h2>
              </header>
              <main id="modal-new-order-complete-content">
                <p>注文が完了しました。</p>
              </main>
              <footer>
                <button className="btn" data-micromodal-close>
                  ＯＫ
                </button>
              </footer>
            </div>
          </div>
        </div>

        <div className="micromodal" id="modal-payment-change" aria-hidden="true">
          <div className="micromodal-overlay" tabIndex={-1} data-micromodal-close>
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-payment-change-title"
            >
              <header>
                <button aria-label="Close modal" data-micromodal-close></button>
                <h2 id="modal-payment-change-title">お支払い方法の変更</h2>
              </header>
              <main id="modal-payment-change-content">
                <p>{paymentType}変更のご案内メールを送付します。</p>
              </main>
              <footer>
                <button className="btn" onClick={paymentMethodUpdate}>
                  送付
                </button>
              </footer>
            </div>
          </div>
        </div>

        <div
            className="micromodal"
            id="modal-payment-change-complete"
            aria-hidden="true"
        >
          <div className="micromodal-overlay" tabIndex={-1} data-micromodal-close>
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-payment-change-complete-title"
            >
              <header>
                <button aria-label="Close modal" data-micromodal-close></button>
                <h2 id="modal-payment-change-complete-title">
                  お支払い方法の変更
                </h2>
              </header>
              <main id="modal-payment-change-content">
                <p>
                  ご登録のメールアドレスに、クレジットカード変更のご案内メールを送付しました。
                  <br />
                  メールに記載のリンクからクレジットカードの変更をしてください。
                </p>
                <p
                    style={{
                      backgroundColor: "#FA45451A",
                      color: "#FA4545",
                      padding: "4px 16px",
                    }}
                >
                  お支払い方法の変更リンクの有効期限はメール送信から約8時間となります。
                  <br />
                  期限が切れた場合はもう一度操作してメールを送信してください。
                </p>
              </main>
              <footer>
                <button className="btn" data-micromodal-close>
                  OK
                </button>
              </footer>
            </div>
          </div>
        </div>

        <div className="micromodal" id="modal-plan-modify" aria-hidden="true">
          <div className="micromodal-overlay" tabIndex={-1} data-micromodal-close>
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-plan-modify-title"
            >
              <header>
                <button aria-label="Close modal" data-micromodal-close></button>
                <h2 id="modal-plan-modify-title">お届け周期の変更</h2>
              </header>
              <main id="modal-plan-modify-content">
                <p>
                  お届け周期を選択してください。
                  <br />
                  ※お届け予定日を変更される場合は、別途、お手続きください。
                </p>
                <select onChange={onChangePlanModify}>{planGroupOptions}</select>
              </main>
              <footer>
                <button
                    className="btn"
                    disabled={pageStatus.planModifyAction}
                    onClick={planModifyAction}
                >
                  変更
                </button>
              </footer>
            </div>
          </div>
        </div>
        <div
            className="micromodal"
            id="modal-plan-modify-complete"
            aria-hidden="true"
        >
          <div className="micromodal-overlay" tabIndex={-1} data-micromodal-close>
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-plan-modify-complete-title"
            >
              <header>
                <button aria-label="Close modal" data-micromodal-close></button>
                <h2 id="modal-plan-modify-complete-title">お届け周期の変更</h2>
              </header>
              <main id="modal-plan-modify-complete-content">
                <p>
                  お届け周期を変更しました。次回ご注文から変更したお届け周期に変更されます。
                  <br />
                  お届け周期：{getPlanModifyCycle()}
                </p>
              </main>
              <footer>
                <button className="btn" data-micromodal-close>
                  ＯＫ
                </button>
              </footer>
            </div>
          </div>
        </div>
        <div
            className="micromodal"
            id="modal-allow-modify-day"
            aria-hidden="true"
        >
          <div className="micromodal-overlay" tabIndex={-1} data-micromodal-close>
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-allow-modify-day-title"
            >
              <header>
                <button aria-label="Close modal" data-micromodal-close></button>
                <h2 id="modal-allow-modify-day-title">{allowModifyDay.title}</h2>
              </header>
              <main id="modal-allow-modify-day-content">
                <p>お届け予定日の変更期限【{rewindNextBillingDate.subtract(1, 'd').format("YYYY年MM月DD日")}】を過ぎております。</p>
                <p>変更は【{dayjs(nextBillingDate).add(1, 'd').format("YYYY年MM月DD日")}】以降にご利用が可能となります。</p>
              </main>
              <footer>
                <button className="btn" data-micromodal-close>
                  ＯＫ
                </button>
              </footer>
            </div>
          </div>
        </div>
        <div className="micromodal" id="modal-stock-error" aria-hidden="true">
          <div className="micromodal-overlay" tabIndex={-1}>
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-stock-error-title"
            >
              <header>
                <button aria-label="Close modal" data-micromodal-close></button>
                <h2 id="modal-stock-error-title">商品の数量変更</h2>
              </header>
              <main id="modal-stock-error-content">
                <p>
                  在庫がありません。
                  <br />
                  入荷をお待ちいただくか、他の商品に変更をお願いします。
                </p>
              </main>
              <footer>
                <button className="btn" data-micromodal-close>
                  ＯＫ
                </button>
              </footer>
            </div>
          </div>
        </div>
        <div
            className="micromodal"
            id="modal-delivery-days-confirm"
            aria-hidden="true"
        >
          <div className="micromodal-overlay" tabIndex={-1}>
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-delivery-days-confirm-title"
            >
              <header>
                <button aria-label="Close modal" data-micromodal-close></button>
                <h2 id="modal-delivery-days-confirm-title">お届け予定日の変更</h2>
              </header>
              <main id="modal-delivery-days-confirm-content">
                <p>
                  次回ご注文、お届け予定日を以下に変更します。
                  <br />
                  よろしいですか？
                  <br />
                  <br />
                  次回お支払い日：
                  {subscription.shop.changeNextBillingDateWithDeliveryDate
                      ? deliveryDate
                          ? deliveryDate!.change
                              .subtract(subscription.shop.deliveryDays, "day")
                              .format("YYYY年M月D日")
                          : ""
                      : dayjs(subscription.nextBillingDate).format("YYYY年M月D日")}
                  <br />
                  次回お届け予定日：
                  {deliveryDate
                      ? deliveryDate!.change.format("YYYY年M月D日")
                      : ""}{" "}
                  {
                    deliveryTimesConstants[
                        deliveryTimesConstants
                            .map((time) => {
                              return time.value;
                            })
                            .indexOf(deliveryDate?.time ?? "")
                        ]?.label
                  }
                </p>
              </main>
              <footer>
                <button className="btn" onClick={updateDeliveryDays}>
                  変更
                </button>
              </footer>
            </div>
          </div>
        </div>
        <div
            className="micromodal"
            id="modal-delivery-days-confirm-noday"
            aria-hidden="true"
        >
          <div className="micromodal-overlay" tabIndex={-1}>
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-delivery-days-confirm-noday-title"
            >
              <header>
                <button aria-label="Close modal" data-micromodal-close></button>
                <h2 id="modal-delivery-days-confirm-noday-title">
                  お届け予定日の変更
                </h2>
              </header>
              <main id="modal-delivery-days-confirm-noday-content">
                <p>
                  次回ご注文のお届け予定日を変更します。
                  <br />
                  ご希望の日を選んで「保存」してください。
                  <br />
                  <br />
                  <div className="checkbox selected">
                    <input
                        id="modal-delivery-days-min-confirm"
                        type="checkbox"
                        disabled={true}
                        checked={true}
                    />
                    <label htmlFor="modal-delivery-days-min-confirm">
                      指定なし
                    </label>
                  </div>
                </p>
              </main>
              <footer>
                <button className="btn" onClick={updateDeliveryDays}>
                  保存
                </button>
              </footer>
            </div>
          </div>
        </div>
      </>
  );
};